import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { title?: string };

function IconFrame({ title, children, ...props }: IconProps) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? "img" : undefined}
      {...props}
    >
      {title ? <title>{title}</title> : null}
      {children}
    </svg>
  );
}

export function WalletIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M4 7.5h15a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-11a2 2 0 0 1 2-2h12"/><path d="M16 12h5v4h-5a2 2 0 0 1 0-4Z"/></IconFrame>;
}

export function ChevronDownIcon(props: IconProps) {
  return <IconFrame {...props}><path d="m7 9.5 5 5 5-5"/></IconFrame>;
}

export function MenuIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M4 6h16M4 12h16M4 18h16"/></IconFrame>;
}

export function SettingsIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M12 15.2a3.2 3.2 0 1 0 0-6.4 3.2 3.2 0 0 0 0 6.4Z"/><path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1-2.8 2.8-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.6v.2h-4V21a1.7 1.7 0 0 0-1-1.6 1.7 1.7 0 0 0-1.9.3l-.1.1L4.2 17l.1-.1a1.7 1.7 0 0 0 .3-1.9A1.7 1.7 0 0 0 3 14H2.8v-4H3a1.7 1.7 0 0 0 1.6-1 1.7 1.7 0 0 0-.3-1.9L4.2 7 7 4.2l.1.1a1.7 1.7 0 0 0 1.9.3A1.7 1.7 0 0 0 10 3V2.8h4V3a1.7 1.7 0 0 0 1 1.6 1.7 1.7 0 0 0 1.9-.3l.1-.1L19.8 7l-.1.1a1.7 1.7 0 0 0-.3 1.9 1.7 1.7 0 0 0 1.6 1h.2v4H21a1.7 1.7 0 0 0-1.6 1Z"/></IconFrame>;
}

export function InfoIcon(props: IconProps) {
  return <IconFrame {...props}><circle cx="12" cy="12" r="9"/><path d="M12 11v5M12 8h.01"/></IconFrame>;
}

export function ArrowUpRightIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M7 17 17 7M8 7h9v9"/></IconFrame>;
}

export function ArrowDownUpIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M7 4v16m-4-4 4 4 4-4M17 20V4m-4 4 4-4 4 4"/></IconFrame>;
}

export function CopyIcon(props: IconProps) {
  return <IconFrame {...props}><rect x="8" y="8" width="11" height="11" rx="1"/><path d="M16 8V5a1 1 0 0 0-1-1H5a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h3"/></IconFrame>;
}

export function DisconnectIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M10 17v2H5V5h5v2M14 8l4 4-4 4M8 12h10"/></IconFrame>;
}

export function BoltIcon(props: IconProps) {
  return <IconFrame {...props}><path d="m13.5 2-9 12h7L10.5 22l9-12h-7Z"/></IconFrame>;
}

export function CpuIcon(props: IconProps) {
  return <IconFrame {...props}><rect x="7" y="7" width="10" height="10" rx="1"/><path d="M9 1v3M15 1v3M9 20v3M15 20v3M20 9h3M20 14h3M1 9h3M1 14h3M10 10h4v4h-4Z"/></IconFrame>;
}

export function GpuIcon(props: IconProps) {
  return <IconFrame {...props}><rect x="3" y="5" width="18" height="13" rx="2"/><circle cx="10" cy="11.5" r="4"/><path d="M21 9h2v5h-2M5 18v2M9 18v2M15 9h3M15 12h3"/></IconFrame>;
}

export function PauseIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M8 5v14M16 5v14"/></IconFrame>;
}

export function PlayIcon(props: IconProps) {
  return <IconFrame {...props}><path d="m8 5 11 7-11 7Z"/></IconFrame>;
}

export function RefreshIcon(props: IconProps) {
  // Lucide refresh-cw (ISC); see public/licenses/lucide.txt.
  return (
    <IconFrame {...props}>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8"/>
      <path d="M21 3v5h-5"/>
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16"/>
      <path d="M8 16H3v5"/>
    </IconFrame>
  );
}

export function CloseIcon(props: IconProps) {
  return <IconFrame {...props}><path d="m6 6 12 12M18 6 6 18"/></IconFrame>;
}

export function LeafIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M20 4C11 4 5 7 5 13c0 3 2 5 5 5 6 0 9-6 10-14Z"/><path d="M4 21c2-5 6-9 12-12"/></IconFrame>;
}

export function ShieldCheckIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M12 3 20 6v6c0 5-3.4 8-8 10-4.6-2-8-5-8-10V6Z"/><path d="m8.5 12 2.2 2.2 4.8-5"/></IconFrame>;
}

export function HourglassIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M6 3h12M6 21h12M7 3c0 5 3 5.5 5 9-2 3.5-5 4-5 9M17 3c0 5-3 5.5-5 9 2 3.5 5 4 5 9"/></IconFrame>;
}

export function ExternalLinkIcon(props: IconProps) {
  return <IconFrame {...props}><path d="M14 4h6v6M20 4l-9 9"/><path d="M18 13v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/></IconFrame>;
}
